import { z } from 'zod';

import { REVIEW_ROSTER_SCOPES } from './Reviews.ts';

/**
 * Account preferences — a person's product choices (a beta opt-in, a table layout), one value per
 * preference per account, readable from any browser or machine.
 *
 * This registry is the only schema. Every change — new key, new values, new default — is a code
 * edit; the account_preferences table never changes. Stored values are validated on write AND on
 * every read, so a value the current registry rejects falls back to the default with no data
 * cleanup. Never reuse a retired key's string; an incompatible meaning change is a new key.
 */

export interface PreferenceDefinition {
  name: string;
  /**
   * Every value is a JSON object — even a lone flag nests (`{ enabled: boolean }`) — so one strict
   * zod schema is the whole write-time contract. Strict, because the stored jsonb must be exactly
   * what the schema admits: an unnamed key is a 400, not silently persisted.
   */
  schema: z.ZodType;
  default: object;
  /** Documentation, not behavior: what the preference controls and where it is read. */
  description: string;
}

/**
 * Ties `default` to the schema's inferred type, which the interface cannot express — a default the
 * schema would reject fails to compile. (Refinements only exist at runtime; the unit test covers
 * those.)
 */
function definePreference<N extends string, S extends z.ZodType>(definition: {
  name: N;
  schema: S;
  default: z.infer<S>;
  description: string;
}) {
  return definition;
}

// The Author table's optional columns; Type, Name and Info are always shown.
export const STUDIO_AUTHOR_COLUMNS = {
  MODULE: 'module',
  OWNER: 'owner',
  CHECKLIST: 'checklist',
  LAST_UPDATED: 'last-updated',
  STATUS: 'status'
} as const;

export type StudioAuthorColumn = (typeof STUDIO_AUTHOR_COLUMNS)[keyof typeof STUDIO_AUTHOR_COLUMNS];

// Keys are optional and unknown ones are dropped so a saved choice survives a column being added
// or retired; readers merge over STUDIO_AUTHOR_COLUMNS_DEFAULT.
const STUDIO_AUTHOR_COLUMN_VISIBILITY = z
  .object({
    [STUDIO_AUTHOR_COLUMNS.MODULE]: z.boolean().optional(),
    [STUDIO_AUTHOR_COLUMNS.OWNER]: z.boolean().optional(),
    [STUDIO_AUTHOR_COLUMNS.CHECKLIST]: z.boolean().optional(),
    [STUDIO_AUTHOR_COLUMNS.LAST_UPDATED]: z.boolean().optional(),
    [STUDIO_AUTHOR_COLUMNS.STATUS]: z.boolean().optional()
  })
  .strip();

export const STUDIO_AUTHOR_COLUMNS_DEFAULT: Record<StudioAuthorColumn, boolean> = {
  [STUDIO_AUTHOR_COLUMNS.MODULE]: true,
  [STUDIO_AUTHOR_COLUMNS.OWNER]: true,
  [STUDIO_AUTHOR_COLUMNS.CHECKLIST]: true,
  [STUDIO_AUTHOR_COLUMNS.LAST_UPDATED]: true,
  [STUDIO_AUTHOR_COLUMNS.STATUS]: true
};

export const PREFERENCES = {
  USE_STUDIO: definePreference({
    name: 'use-studio',
    schema: z.object({ enabled: z.boolean() }).strict(),
    default: { enabled: false },
    description: 'Opt in to the Studio authoring surface while it is in beta.'
  }),
  STUDIO_AUTHOR_COLUMNS: definePreference({
    name: 'studio-author-columns',
    schema: z
      .object({
        myWork: STUDIO_AUTHOR_COLUMN_VISIBILITY,
        allFiles: STUDIO_AUTHOR_COLUMN_VISIBILITY
      })
      .strict(),
    default: {
      myWork: { ...STUDIO_AUTHOR_COLUMNS_DEFAULT },
      allFiles: { ...STUDIO_AUTHOR_COLUMNS_DEFAULT }
    },
    description:
      'Which optional columns the Studio Author table shows, kept separately for My Work and for All Files and module views.'
  }),
  REVIEWER_GROUPS_BANNER_DISMISSED: definePreference({
    name: 'reviewer-groups-banner-dismissed',
    schema: z.object({ dismissed: z.boolean() }).strict(),
    default: { dismissed: false },
    description:
      'Hides the "anyone with author access can see these groups" notice on the Studio Reviewer Groups section once dismissed.'
  }),
  STUDIO_MANAGE_REVIEWS_SCOPE: definePreference({
    name: 'studio-manage-reviews-scope',
    schema: z
      .object({
        scope: z.enum([REVIEW_ROSTER_SCOPES.MINE, REVIEW_ROSTER_SCOPES.ORGANIZATION])
      })
      .strict(),
    default: { scope: REVIEW_ROSTER_SCOPES.MINE },
    description:
      'Whether the Studio Manage Reviews roster shows the reviews the viewer sent or owns, or every review in their organization (org admins and managers only).'
  })
} as const satisfies Record<string, PreferenceDefinition>;

export const PREFERENCE_DEFINITIONS: readonly PreferenceDefinition[] = Object.values(PREFERENCES);

export function findPreference(name: string): PreferenceDefinition | undefined {
  return PREFERENCE_DEFINITIONS.find(preference => preference.name === name);
}

/** The runtime type a definition admits. */
export type PreferenceValue<D extends PreferenceDefinition> = z.infer<D['schema']>;
